import {put, call} from 'redux-saga/effects';
import Discount from "../../entities/discount";
import * as Actions from "../actions/discountActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DiscountAction = {
    discountId: string;
    discount: Discount;
    discounts: Discount[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDiscount(action: DiscountAction) {
	const session = tabSession.captureGeneration();
	try {
		const discount: Discount = yield call(api.findOne, 'discount', action.discountId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_FETCH_ONE_SUCCESS, discountId: action.discountId, discount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_FETCH_ONE_FAILURE, discountId: action.discountId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DiscountAction} action
 */
export function* saveDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DISCOUNT_SAVE saga', action.discount?.id);
    
	if (action.discount === undefined || action.discount === null) {
		console.error('saveDiscount received a null');
		throw new Error('saveDiscount received a null');
	}

	if (!(action.discount instanceof Discount)) {
		action.discount = new Discount(action.discount);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'discount', action.discount);
		if (addResult.isErr()) {
			console.error(`⮂ saveDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DISCOUNT_SAVE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_SAVE, discount: action.discount});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DISCOUNT_SAVE_FAILURE, discount: action.discount, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {DiscountAction} action
 * @param {Discount[]} action.discounts
 */
export function* saveManyDiscounts(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DISCOUNT_SAVE_MANY saga', action.discounts.map(discount => discount.id).join(','));
    
    if (!Array.isArray(action.discounts)) {
        console.error('saveManyDiscounts expected an array, received', action.discounts);
        throw new Error('saveManyDiscounts expected an array');
    }

	try {
		const toSave: Discount[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.discounts.length; i++) {
			const discount = action.discounts[i];
			discount.persisted = false;
			toSave.push(discount);
		}

		const addResult = yield call(idb.add, 'discount', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyDiscounts: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DISCOUNT_SAVE_MANY_FAILURE, discounts: action.discounts, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_SAVE_MANY, discounts: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DISCOUNT_SAVE_MANY_FAILURE, discounts: action.discounts, status: 500, err});

		console.error(`Error saving ${action.discounts.length} discounts to database`, err);
	}
}

/**
 * @param {DiscountAction} action
 */
export function* removeDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.discount === undefined || action.discount === null || !(action.discount instanceof Discount)) {
			console.error('removeDiscount expected type Discount, received', action.discount);
			throw new Error('removeDiscount expected type Discount');
		}

	try {
		// Mark as deleted
		action.discount.status = 'D';
		action.discount.addedOn = newHRTimestamp();
		action.discount.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'discount', action.discount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DISCOUNT_REMOVE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_REMOVE, discount: action.discount});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_REMOVE_FAILURE, discount: action.discount, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Discount.
	*
	* @param {DiscountAction} action
	*/
export function* restoreDiscount(action: DiscountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDiscount expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.discount === undefined || action.discount === null) {
        console.error('restoreDiscount expected a Discount, received null');
        throw new Error('restoreDiscount expected a Discount, received null');
    }
    else if (!(action.discount instanceof Discount)) action.discount = new Discount(action.discount); 
    
	try {
		// Mark as restored
		action.discount.status = 'U';
		action.discount.addedOn = newHRTimestamp();
		action.discount.addedBy = userId;
		action.discount.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'discount', action.discount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDiscount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DISCOUNT_RESTORE_FAILURE, discount: action.discount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DISCOUNT_RESTORE, discount: action.discount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DISCOUNT_RESTORE_FAILURE, discount: action.discount, status: 500, err});

		console.error('Error restoring from database', err);
	}
}